import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DOCUMENT,
  Inject,
  inject,
  makeStateKey,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  StateKey,
  TransferState} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Params,
  Router,
  RouterOutlet,
} from '@angular/router';
import {
  distinctUntilChanged,
  filter,
  first,
  interval,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throttle,
  throttleTime,
  withLatestFrom,
  zip,
} from 'rxjs';

import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';

import { ApplicationUpdateService, appVersion, REQUEST } from '@personal-area/common';
import { AuthService } from '@personal-area/global/authorization/services';
import { BloomreachBannerContainer, BloomreachService } from '@personal-area/global/bloomreach';
import { MobileDetection } from '@personal-area/global/browser-detection';
import { TypeConverter } from '@personal-area/global/common';
import { Countries } from '@personal-area/global/country';
import { EventType, getPersonTypeFromRole, GTMService } from '@personal-area/global/gtm';
import { LoaderModule } from '@personal-area/global/http/ui';
import { I18nService, siteLanguageKey } from '@personal-area/global/i18n';
import { LoginAnalyticsFacade } from '@personal-area/global/login-analytics';
import { PageSpeedService } from '@personal-area/global/navigation';
import { TitleService } from '@personal-area/global/navigation-layout';
import {
  ReferralComposingService,
  ReferralConnectionService,
  ReferralService,
  ReferralStorageService,
} from '@personal-area/global/referral';
import { paramsKey } from '@personal-area/global/registration';
import { ComposedReferralData, LoggedUser, Role, Type } from '@personal-area/global/users/models';
import { UserApiService, UserFacade, UserService } from '@personal-area/global/users/services';
import { LastLoginService } from '@personal-area/shared/authorization';
import { CryptoStorageService, LOCAL_STORAGE, SESSION_STORAGE, UniversalStorageService } from '@personal-area/storage';

import { GetFeedbackService } from './core/services/getfeedback/getfeedback.service';
import { CanonicalService } from './core/services/system/canonical.service';
import { LoginAnalyticsTrackingService } from './core/services/system/login-analytics-tracking.service';
import { MetaTagService } from './core/services/system/meta-tag.service';
import { Hotjar } from './core/utility/hotjar';
import { MaintenanceService } from './maintenance/services/maintenance.service';
import { HeaderComponent } from './shared/header/header.component';
import { OldBrowserComponent } from './shared/old-browser/old-browser.component';

export let isJsEnabled = false;

@Component({
    selector: 'global-personal-area-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
    LoaderModule,
    TranslateModule,
    BloomreachBannerContainer,
    OldBrowserComponent,
    HeaderComponent,
    RouterOutlet
]
})
export class AppComponent implements OnInit {
  private _appUpdateService = inject(ApplicationUpdateService);
  private _document = inject(DOCUMENT);
  private _req = inject(REQUEST);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _lastLoginService = inject(LastLoginService);
  private readonly _localStorage = inject(LOCAL_STORAGE);

  public showHeader = false;
  public showWarning = false;
  public isAuthorizedHeaderLocked: boolean;
  public isHeaderNavigationLocked: boolean;

  public isWhiteBg = false;
  public noMobileFooter = false;

  public isLangLoaded: boolean;
  public isNavigationInProgress: boolean;
  public isCache: boolean;
  public bannerId = '';

  protected _isUserLoaded$: Observable<boolean>;

  private _referralUpdated = new Subject<void>();
  private _user$: Observable<LoggedUser>;

  constructor(
    protected _router: Router,
    protected _titleService: TitleService,
    protected _i18nService: I18nService,
    protected _activatedRoute: ActivatedRoute,
    protected _translateService: TranslateService,
    protected _authService: AuthService,
    protected _gtmService: GTMService,
    protected _bloomreachService: BloomreachService,
    protected _renderer: Renderer2,
    protected _userApiService: UserApiService,
    protected _transferState: TransferState,
    protected _metaTagService: MetaTagService,
    protected _dialog: MatDialog,
    protected _cdRef: ChangeDetectorRef,
    protected _referralService: ReferralService,
    protected _referralComposingService: ReferralComposingService,
    protected _referralStorageService: ReferralStorageService,
    protected _referralConnectionService: ReferralConnectionService,
    protected _store: Store,
    protected _maintenanceService: MaintenanceService,
    protected _userFacade: UserFacade,
    protected _userService: UserService,
    protected _canonicalService: CanonicalService,
    protected _loginAnalyticsFacade: LoginAnalyticsFacade,
    protected _loginAnalyticsTrackingService: LoginAnalyticsTrackingService,
    protected _cookieService: UniversalStorageService,
    protected _getFeedbackService: GetFeedbackService,
    protected _cryptoStorageService: CryptoStorageService,
    @Inject(SESSION_STORAGE) private _sessionStorage: Storage,
  ) {
    inject(PageSpeedService);

    console.log(`Personal Area Version: ${appVersion()}`);

    const key: StateKey<boolean> = makeStateKey<boolean>(`transfer-is-cache`);

    if (isPlatformServer(this._platformId)) {
      this._transferState.set<boolean>(key, true);

      if (environment.hotjar.enable && Hotjar.isEnabled(this._req.path)) {
        const s = this._renderer.createElement('script');
        s.id = Hotjar.id;
        s.innerHTML = Hotjar.getScript(
          environment.hotjar.trackingId,
          environment.hotjar.version,
        );
        this._renderer.insertBefore(
          this._document.head,
          s,
          this._document.head.firstChild,
        );
      }

      if (this._gtmService.isEnabled) {
        const s = this._renderer.createElement('script');
        s.id = GTMService.id;
        s.innerHTML = GTMService.getScript(environment.gtm.trackingId);
        this._renderer.insertBefore(
          this._document.head,
          s,
          this._document.head.firstChild,
        );
      }
    }

    isJsEnabled = isPlatformBrowser(this._platformId);

    this.isCache = this._transferState.get<boolean>(key, false);
    this.isLangLoaded = isPlatformBrowser(this._platformId) && this.isCache;

    this._user$ = this._userFacade.user$;
    this._isUserLoaded$ = this._userFacade.isUserLoaded$;
  }

  get isMobile(): boolean {
    return MobileDetection.isMobile();
  }

  public ngOnInit(): void {
    this._canonicalService.addCanonicalIfNeeded();

    if (isPlatformBrowser(this._platformId)) {
      if (environment.bloomreach.enable) {
        this._bloomreachService.createSession();
      }

      this._getFeedbackService.init();

      this._referralService.init();

      this._activatedRoute.queryParams.subscribe((params: Params) => {
        this._referralService.saveParams(this._authService.isLoggedIn, params);
      });
    }

    this._checkGeoLocation();


    this._authService.onLogout
      .pipe(
        tap(() => {
          this._dialog.closeAll();
          this._referralService.clearUtmParams(
            this._userFacade.loggedUser.role === Role.TradingPlatform,
          );
          this._referralService.clearReferrerName();
          this._cookieService.removeItem('userId');
          this._cryptoStorageService.removeItem(
            paramsKey,
            this._sessionStorage,
          );
        }),
        switchMap(() => this._maintenanceService.getMaintenanceEnd(true)),
        filter(Boolean),
      )
      .subscribe(() => {
        /* use location.href because auth.service has subscription with router navigate to /login
         and it could be done faster then other onLogout subscriptions */
        window.location.href = window.location.origin + '/cabinet/maintenance';
      });

    const onNavigationEnd$ = this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    );

    this._router.events
      .pipe(
        filter(
          (evt) =>
            evt instanceof NavigationStart || evt instanceof NavigationEnd,
        ),
        tap((evt) => {
          if (
            evt instanceof NavigationStart &&
            this._appUpdateService.isReloadRequired
          ) {
            window.location.href =
              window.location.origin + '/cabinet' + evt.url;
          }

          if (evt instanceof NavigationEnd) {
            this.bannerId = '';
            if (evt['url'].includes('/login')) {
              this.bannerId = 'login';
            }
            if (evt['url'].includes('/registration')) {
              this.bannerId = 'registration';
            }
          }
        }),
        throttleTime(200),
      )
      .subscribe((evt) => {
        let route = this._activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }

        this.isNavigationInProgress =
          evt instanceof NavigationStart &&
          !route.routeConfig?.data?.skipNavigationLoader;
      });

    this._router.events
      .pipe(
        filter(
          (evt) =>
            evt instanceof NavigationEnd ||
            evt instanceof NavigationCancel ||
            evt instanceof NavigationError,
        ),
      )
      .subscribe(() => (this.isNavigationInProgress = false));

    // Подписка на навигацию и смену языка
    merge(this._translateService.onLangChange, onNavigationEnd$)
      .pipe(
        map(() => {
          let route = this._activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
      )
      .subscribe((data) => {
        const title = data['title'];
        if (title && !data['postponeGtmPageView']) {
          this._titleService.setTitle(
            this._translateService.instant(title),
            false,
          );
        }
        const type = data['personType'];
        if (type) {
          this._gtmService.setPersonType(
            getPersonTypeFromRole(type === Type.Demo ? Type.Trader : type),
          );
        }

        // скрыть/показать блок header (показываем если параметра нет или он = true)
        this.showHeader = data['showHeader'] ?? true;

        this.showWarning =
          isPlatformBrowser(this._platformId) && data['showWarning'];
        this.isAuthorizedHeaderLocked = data['isHeaderLocked'];
        this.isHeaderNavigationLocked = data['isHeaderNavigationLocked'];

        this.isWhiteBg = data['isWhiteBg'];
        this.noMobileFooter = data['noMobileFooter'];

        if (!data['isVisibleForBots']) {
          this._metaTagService.setRobotsTag('noindex');
        }

        if (data.metaTags) {
          this._metaTagService.setSocialMediaTags(
            this._router.url,
            data.metaTags.title,
            data.metaTags.description,
            data.metaTags.image,
          );
        }

        if (data.noCache) {
          this._metaTagService.setNoCacheTags();
        }
      });

    let currentUrl: string = this._req.path;
    // Подписка на навигацию
    onNavigationEnd$
      .pipe(
        switchMap((event: NavigationEnd) => {
          currentUrl = event.url;
          let route = this._activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route.data;
        }),
      )
      .subscribe((data) => {
        this.isNavigationInProgress = false;

        if (!data['postponeGtmPageView']) {
          if (isPlatformBrowser(this._platformId)) {
            this._gtmService.page(
              window.location.pathname + window.location.search,
            );
          } else {
            this._gtmService.serverPage(
              this._renderer,
              '/cabinet' + currentUrl.split('?')[0],
            );
          }
        }
      });

    zip(
      onNavigationEnd$.pipe(
        map(() => {
          let route = this._activatedRoute.snapshot;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route.data;
        }),
        filter((data) => Boolean(data?.postponeGtmPageView)),
      ),
      this._titleService.lastPostponedTitle$.pipe(
        filter((title) => Boolean(title)),
      ),
    ).subscribe(() => {
      this._gtmService.page(window.location.pathname + window.location.search);
    });

    this._i18nService.translationLoaded$.subscribe((language) => {
      this.isLangLoaded = true;
      const lang = language.slice(0, 2);
      const setDir = (dir: 'rtl' | 'ltr'): void =>
        this._renderer.setAttribute(
          this._document.getElementsByTagName('html')[0],
          'dir',
          dir,
        );

      setDir(this._i18nService.isRtl ? 'rtl' : 'ltr');

      this._renderer.setAttribute(
        this._document.getElementsByTagName('html')[0],
        'lang',
        lang,
      );
    });

    const loggedUser = this._userFacade.loggedUser;

    if (this._authService.isLoggedIn) {
      this._bloomreachService.initialized
        .pipe(
          first((initialized) => initialized === true),
          tap(() =>
            this._bloomreachService.identifyUserAndSubscribe(
              this._authService.userId,
            ),
          ),
        )
        .subscribe();
    }

    if (this._gtmService.isEnabled) {
      if (!this.isLangLoaded) {
        // Wait translations loaded
        this._gtmService.addWaitFactor(this._i18nService.translationLoaded$);
      }

      this._setNonAuthorizedUserGTMData(loggedUser);

      if (this._authService.isLoggedIn) {
        this._gtmService.userData = {
          // eslint-disable-next-line
          auth_status: 1,
          id: this._authService.userId,
        };

        // Wait until user is loaded
        this._gtmService.addWaitFactor(
          this._isUserLoaded$.pipe(
            distinctUntilChanged(),
            filter((flag) => flag),
          ),
        );
      }

      // Отправка последних транзакций
      this._gtmService.afterPageView
        .pipe(
          filter(
            () =>
              this._authService.isLoggedIn &&
              loggedUser.extras &&
              loggedUser.extras.depositInfo.depositsCount > 0,
          ),
          throttle(() => interval(10000)),
          switchMap(() =>
            this._userApiService
              .lastDeposits(this._authService.userId)
              .pipe(filter((l) => !!l && l.items.length > 0)),
          ),
        )
        .subscribe((list) => {
          list.items.forEach((t) => {
            this._gtmService.event(EventType.Transaction, {
              transactionValue: TypeConverter.numberToMoney(t.amountUsd, 2),
              transactionId: t.id.toString(),
              isFirstDeposit: t.isFirstDeposit,
            });
          });
        });

      this._user$
        .pipe(
          withLatestFrom(this._isUserLoaded$),
          filter(([, flag]) => flag),
        )
        .subscribe(([user]) => {
          this._gtmService.setUser(user, Countries.findA2Code(user.country));
          this._loginAnalyticsFacade.setUserData(this._gtmService.data.user);
        });

      this._authService.onLogout.subscribe(() => {
        this._gtmService.clearData();
        this._setNonAuthorizedUserGTMData(loggedUser);
      });

      this._translateService.onLangChange.subscribe(
        (event: LangChangeEvent) => {
          this._gtmService.userData = {
            language: event.lang.includes('-')
              ? event.lang.split('-')[0]
              : event.lang,
          };
        },
      );
    }

    this._authService.onLogin.subscribe((token) => {
      if (this._bloomreachService.initialized.value) {
        this._bloomreachService.identifyUserAndSubscribe(
          this._authService.userId,
        );
      }
      this._userFacade.updateUserOptions(this._userService.getPersonType(token));

      this._loginAnalyticsFacade.setIsLoginAfterRegistration(
        token.isLoginAfterReg,
      );

      if (token.loginMethod) {
        this._lastLoginService.save(token.loginMethod, token.loginEmail);
      }
    });

    // update settings when user is loaded ( once in a session )
    this._isUserLoaded$
      .pipe(
        distinctUntilChanged(),
        filter((flag) => flag),
      )
      .subscribe(() => {
        const savedLanguage = this._localStorage.getItem(siteLanguageKey);

        this._userService.updateUserSettings(
          savedLanguage,
          this._gtmService.currentGoogleClientId,
        );
        if (
          !savedLanguage &&
          this._i18nService.languageA2 !== this._userFacade.loggedUser.language
        ) {
          this._i18nService.language = this._userFacade.loggedUser.language;
        }
      });

    this._user$
      .pipe(
        withLatestFrom(this._isUserLoaded$),
        filter(([{ isEnabled }, isLoaded]) => !isEnabled && isLoaded),
      )
      .subscribe(() => this._authService.logout());

    this._router.events
      .pipe(
        filter(
          (e) => this._authService.isLoggedIn && e instanceof NavigationEnd,
        ),
        switchMap(() => this._activatedRoute.queryParams),
        filter(Boolean),
        switchMap(() => this._updateReferral(false)),
        takeUntil(this._referralUpdated),
      )
      .subscribe(() => {
        this._referralUpdated.next();
        this._referralUpdated.complete();
      });

    this._authService.onLogin
      .pipe(
        switchMap((data) => this._updateReferral(data.isLoginAfterReg)),
        takeUntil(this._referralUpdated),
      )
      .subscribe();

    this._loginAnalyticsFacade
      .getLoginAnalyticsData()
      .pipe(
        filter(
          (data) =>
            data?.user !== null &&
            data?.isLoginAfterRegistration !== null &&
            !data?.isEventSent,
        ),
      )
      .subscribe(({ user, isLoginAfterRegistration, method }) => {
        if (isLoginAfterRegistration) {
          this._loginAnalyticsTrackingService.trackSignUp(method, user);
          this._loginAnalyticsFacade.setEventDispatchStatus(true);
          return;
        }

        this._loginAnalyticsTrackingService.trackLogin(method, user);
        this._loginAnalyticsFacade.setEventDispatchStatus(true);
      });

    this._translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this._userService.updateUserSettings(
        event.lang,
        this._gtmService.currentGoogleClientId,
      );
    });

    this._authService.loginMethod$.subscribe((method) =>
      this._loginAnalyticsFacade.setLoginMethodFromLoginData(method),
    );
  }

  public rtlStatus(): boolean {
    return this._i18nService.isRtl;
  }

  private _updateReferral = (loginAfterReg: boolean) => {
    const currentReferralParams =
      this._userFacade.loggedUser.referralLinkParams;
    const newUrlReferralParams =
      this._referralComposingService.getComposedUrlReferralData(
        this._i18nService.languageA2,
      );
    const newStoredReferralParams = this._referralStorageService.load();
    const referralParamsForUpdate =
      newUrlReferralParams || newStoredReferralParams;
    if (
      referralParamsForUpdate &&
      (!ComposedReferralData.isEqual(
          currentReferralParams,
          referralParamsForUpdate,
        ) ||
        !currentReferralParams) &&
      !loginAfterReg
    ) {
      this._referralStorageService.save(referralParamsForUpdate);
      return this._referralConnectionService.connectToPartner(loginAfterReg);
    } else {
      return of(null);
    }
  };

  private _setNonAuthorizedUserGTMData(user: LoggedUser): void {
    this._gtmService.userData = {
      auth_status: '0',
      country: user.country,
      language: this._i18nService.languageA2,
      person_type: user.role,
    };
  }

  private _checkGeoLocation() {
    let location = isPlatformBrowser(this._platformId)
      ? this._cookieService.getItem('user_location')
      : this._req.headers['x-user-location'];

    if (!location || location.includes('--')) {
      location = environment.defaultLocation;
    }

    this._userFacade.updateUserOptions({ location: location as string });
  }
}
